import React, { useState, useEffect } from "react";
import Dropdown from "../../dropdown/dropdown";
import ComparisonBarChart from "../../charts/comparison-bar-chart";
import useLocationStore from "../config/use-location-store";
import { instanceMob } from "../../../utils/utils";
import CreateGoalModal from "../modals/create-goal-modal";

const monthMap = {
    january: "Ene",
    february: "Feb",
    march: "Mar",
    april: "Abr",
    may: "May",
    june: "Jun",
    july: "Jul",
    august: "Ago",
    september: "Sep",
    october: "Oct",
    november: "Nov",
    december: "Dic",
};

const emptyChartData = Object.values(monthMap).map((m) => ({
    name: m,
    value1: 0,
    value2: 0,
}));

const ProfitGoalsPanel = () => {
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [goalOptions, setGoalOptions] = useState([]);
    const [chartData, setChartData] = useState(emptyChartData);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const { selectedLocation } = useLocationStore();

    const fetchGoals = () => {
        if (!selectedLocation) return;

        setLoading(true);
        instanceMob
            .get(`admin/accounting/goal/list?location_id=${selectedLocation.id}`)
            .then((res) => {
                const data = res.data || [];
                const formatted = data.map((goal) => ({
                    name: goal?.goal_type_name || goal?.goal_type,
                    id: goal.id,
                }));
                setGoalOptions(formatted);
                if (formatted.length > 0) {
                    setSelectedGoal(formatted[0]);
                }
            })
            .catch((error) => {
                console.error("❌ Error al obtener metas:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchGoals();
    }, [selectedLocation]);

    useEffect(() => {
        if (!selectedGoal || !selectedGoal.id) return;

        setLoading(true);

        instanceMob
            .get(`admin/accounting/goal/vs?goal_id=${selectedGoal.id}`)
            .then((res) => {
                const data = res.data;

                if (!data || !data.months) {
                    setChartData([]);
                    return;
                }

                const formattedData = data.months.map((m) => ({
                    name: monthMap[m.month.toLowerCase()] || m.month,
                    value1: m.real_value || 0,
                    value2: m.goal_value || 0,
                }));

                setChartData(formattedData);
            })
            .catch((error) => {
                console.error("❌ Error al obtener datos del gráfico:", error);
                setChartData([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [selectedGoal]);

    return (
        <>
            <div className="accounting-dashboard-profit-goals d-flex flex-column gap-2">
                <div className="d-flex flex-row gap-2 align-items-center justify-content-end">
                    <button type="button" className="btn accounting-dashboard-profit-goals-btn"
                        onClick={() => setShowCreateModal(true)}>
                        Añadir Meta
                    </button>
                </div>

                <div className="accounting-dashboard-profit-goals-container d-flex flex-column p-4 py-4 gap-4">
                    <div className="d-flex flex-row align-items-center justify-content-between">
                        <div className="d-flex flex-row align-items-center gap-3">
                            <div className="accounting-dashboard-profit-goals-title">
                                Ventas VS Metas
                            </div>
                            <Dropdown
                                items={goalOptions}
                                onSelect={(item) => setSelectedGoal(item)}
                                placeholder="Selecciona un tipo de meta"
                                showLabel={false}
                                width={220}
                                initialValue={goalOptions[0]?.name}
                                key={goalOptions.length}
                            />
                        </div>

                        <div className="d-flex flex-row align-items-center gap-3">
                            <div className="d-flex flex-row align-items-center gap-1">
                                <div className="accounting-dashboard-profit-goals-dot blue" />
                                <div className="accounting-dashboard-profit-goals-label">Ventas</div>
                            </div>
                            <div className="d-flex flex-row align-items-center gap-1">
                                <div className="accounting-dashboard-profit-goals-dot green" />
                                <div className="accounting-dashboard-profit-goals-label">Meta</div>
                            </div>
                        </div>
                    </div>

                    <ComparisonBarChart
                        height={260}
                        seriesKeys={["value1", "value2"]}
                        seriesLabels={["Ventas", "Meta"]}
                        showLines={false}
                        data={chartData}
                        loading={loading}
                    />
                </div>
            </div>

            <CreateGoalModal
                show={showCreateModal}
                onClose={(shouldRefresh, editedGoalType) => {
                    setShowCreateModal(false);

                    if (shouldRefresh) {
                        fetchGoals();
                        if (editedGoalType && selectedGoal && selectedGoal.name === editedGoalType.label) {
                            instanceMob
                                .get(`admin/accounting/goal/vs?goal_id=${selectedGoal.id}`)
                                .then((res) => {
                                    const data = res.data;
                                    if (!data || !data.months) return setChartData([]);

                                    const formattedData = data.months.map((m) => ({
                                        name: monthMap[m.month.toLowerCase()] || m.month,
                                        value1: m.real_value || 0,
                                        value2: m.goal_value || 0,
                                    }));
                                    setChartData(formattedData);
                                })
                                .catch((err) => console.error("❌ Error al refrescar gráfica:", err));
                        }
                    }
                }}
                locationName={selectedLocation?.name || ""}
            />
        </>

    );
};

export default ProfitGoalsPanel;
